import styled, { keyframes } from 'styled-components';

import { Typography } from '@/components/ui/Typography/Typography.styles';
import { CONTAINER_BREAKPOINTS, CONTAINER_MAX } from '@/constants/breakpoints';
import {
  containerPaddingInline,
  flexBetween,
  flexCenterAligned
} from '@/styles/common';

const loading = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

export const Wrapper = styled.header<{
  $isBlured: boolean;
  $hideOnMobile?: boolean;
}>`
  position: fixed;
  top: var(--banner-header-offset, 0px);
  left: 0;
  width: 100%;
  ${flexBetween};
  height: 75px;
  border-bottom: 1px solid var(--border-main);
  ${containerPaddingInline};
  background: ${({ $isBlured }) =>
    $isBlured ? 'var(--header-scrolled-bg)' : 'var(--bg-main)'};
  transition: background 0.3s ease;
  z-index: 4;
  gap: 25px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    backdrop-filter: blur(15px);
    -webkit-backdrop-filter: blur(15px);
    opacity: ${({ $isBlured }) => ($isBlured ? 1 : 0)};
  }

  @media (max-width: ${CONTAINER_MAX.tabletL}) {
    padding-block: 0;
    min-height: 75px;
    gap: 12px;
  }

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    `@media (max-width: ${CONTAINER_MAX.tablet}) { display: none; }`}
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    gap: 20px;
  }

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    gap: 16px;
    width: 100%;
    justify-content: space-between;
    min-height: 58px;
  }
`;

export const MenuAndLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const Navbar = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
`;

export const CenterSection = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 300px;
  position: relative;

  display: flex;
  justify-content: flex-end;

  @media (max-width: ${CONTAINER_MAX.tabletL}) {
    max-width: 100%;
  }
`;

export const TabletThemeSwitcher = styled.div`
  display: none;

  @media (max-width: ${CONTAINER_MAX.tabletL}) {
    display: flex;
    align-items: center;
  }

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    display: none;
  }
`;

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: ${CONTAINER_BREAKPOINTS.tablet}) and (max-width: ${CONTAINER_MAX.tabletL}) {
    gap: 16px;
  }

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    display: none;
  }
`;

export const DesktopOnlyTheme = styled.div`
  @media (max-width: ${CONTAINER_MAX.tabletL}) {
    display: none;
  }
`;

export const ButtonsAndSearchWrapper = styled.div<{ $isInvisible: boolean }>`
  display: ${({ $isInvisible }) => ($isInvisible ? 'none' : 'flex')};
  align-items: center;
  gap: 25px;

  @media (min-width: ${CONTAINER_BREAKPOINTS.tablet}) and (max-width: ${CONTAINER_MAX.tabletL}) {
    gap: 16px;
  }
`;

export const GoToBookmarksButton = styled.button<{ $isActive: boolean }>`
  ${flexCenterAligned};
  width: 44px;
  height: 44px;
  background: transparent;
  border: 1px solid var(--border-main);
  border-radius: 28px;
  cursor: pointer;

  svg {
    transition: all 0.3s ease;
    color: ${({ $isActive }) =>
      $isActive ? 'var(--system-red)' : 'var(--textAndLabels-main)'};
    fill: ${({ $isActive }) =>
      $isActive ? 'var(--system-red)' : 'transparent'};
  }

  border-color: ${({ $isActive }) =>
    $isActive ? 'var(--system-red)' : 'var(--border-main)'};

  transition: background 0.3s ease;

  &:hover {
    background: var(--bg-secondary);
  }
`;

export const SearchButton = styled.button`
  ${flexCenterAligned};
  width: 40px;
  height: 40px;
  border-radius: 100px;
  background: var(--bg-card);
  cursor: pointer;
  border: none;
  margin-top: 15px;
  margin-bottom: 15px;
  & > * {
    color: var(--textAndLabels-main);
  }
`;

export const ProgressBar = styled.div<{ $isLoading: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: transparent;
  z-index: 5;
  display: ${({ $isLoading }) => ($isLoading ? 'block' : 'none')};

  &::before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--custom-yellow);
    animation: ${loading} 1.8s infinite linear;
  }
`;

export const RightContentWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  justify-content: end;
  gap: 25px;

  @media (min-width: ${CONTAINER_BREAKPOINTS.tablet}) and (max-width: ${CONTAINER_MAX.tabletL}) {
    gap: 16px;
  }
`;

export const FullAddLabel = styled(Typography)`
  @media (max-width: ${CONTAINER_MAX.desktop}) {
    display: none;
  }
`;

export const ShortAddLabel = styled(Typography)`
  display: none;

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    display: block;
  }
`;

export const TabletParticipantDropdown = styled.div`
  display: none;

  @media (min-width: ${CONTAINER_BREAKPOINTS.tablet}) and (max-width: ${CONTAINER_MAX.tabletL}) {
    display: flex;
    align-items: center;
  }
`;

export const LoginButtonWrapper = styled.div`
  @media (max-width: ${CONTAINER_MAX.tablet}) {
    display: none;
  }
`;

export const DesktopParticipantTabs = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${CONTAINER_MAX.tabletL}) {
    display: none;
  }
`;
